import React, { useState, useEffect, useRef } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import remarkMath from 'remark-math';
import rehypeKatex from 'rehype-katex';
import rehypeHighlight from 'rehype-highlight';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { oneDark } from 'react-syntax-highlighter/dist/esm/styles/prism';
import { Copy, Check, Download, Maximize2 } from 'react-feather';
import 'katex/dist/katex.min.css';

// Custom Code Block Component with Copy Functionality
const CodeBlock = ({ children, className, node, ...props }) => {
  const [copied, setCopied] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);
  const match = /language-(\w+)/.exec(className || '');
  const language = match ? match[1] : '';
  
  // Robust code extraction - handle any type of children
  let code = '';
  try {
    if (typeof children === 'string') {
      code = children;
    } else if (Array.isArray(children)) {
      code = children.map(child => {
        if (typeof child === 'string') return child;
        if (child && typeof child === 'object' && child.props && child.props.children) {
          return String(child.props.children);
        }
        return String(child);
      }).join('');
    } else if (children && typeof children === 'object') {
      // Handle React element or object
      if (children.props && children.props.children) {
        code = String(children.props.children);
      } else {
        code = JSON.stringify(children, null, 2);
      }
    } else {
      code = String(children || '');
    }
    code = code.replace(/\n$/, '');
  } catch (err) {
    console.error('Error processing code block:', err);
    code = String(children || '');
  }

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(code);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy code:', err);
    }
  };

  const handleDownload = () => {
    const blob = new Blob([code], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `code.${language || 'txt'}`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  if (language) {
    return (
      <div className="code-block-container my-4 w-full overflow-hidden rounded-xl border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900">
        {/* Header */}
        <div className="flex items-center justify-between bg-gray-100 dark:bg-gray-800 px-4 py-2 border-b border-gray-200 dark:border-gray-700">
          <span className="text-sm font-mono text-gray-600 dark:text-gray-400 lowercase">
            {language}
          </span>
          <div className="flex items-center gap-2">
            <button
              onClick={handleDownload}
              className="p-1 text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200 transition-colors rounded"
              title="Download code"
            >
              <Download size={16} />
            </button>
            <button
              onClick={() => setIsExpanded(!isExpanded)}
              className="p-1 text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200 transition-colors rounded"
              title="Expand code"
            >
              <Maximize2 size={16} />
            </button>
            <button
              onClick={handleCopy}
              className="p-1 text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200 transition-colors rounded"
              title="Copy code"
            >
              {copied ? <Check size={16} className="text-green-500" /> : <Copy size={16} />}
            </button>
          </div>
        </div>
        
        {/* Code Content */}
        <div className={`overflow-x-auto ${isExpanded ? 'max-h-none' : 'max-h-96'}`}>
          <SyntaxHighlighter
            style={oneDark}
            language={language}
            PreTag="div"
            customStyle={{
              margin: 0,
              padding: '1rem',
              background: 'transparent',
              fontSize: '0.875rem',
              lineHeight: '1.5',
            }}
            codeTagProps={{
              style: {
                fontFamily: 'ui-monospace, SFMono-Regular, "SF Mono", Consolas, "Liberation Mono", Menlo, monospace',
              }
            }}
          >
            {code}
          </SyntaxHighlighter>
        </div>
      </div>
    );
  }

  // Inline code
  return (
    <code 
      className="bg-gray-100 dark:bg-gray-800/80 text-blue-600 dark:text-blue-400 px-1.5 py-0.5 rounded-md text-[13px] font-mono border border-gray-200 dark:border-gray-700/50"
      {...props}
    >
      {children}
    </code>
  );
};

// Custom Table Component - enhanced for comparison tables
const TableComponent = ({ children, ...props }) => (
  <div className="my-6 overflow-x-auto rounded-lg border border-gray-200/80 dark:border-gray-700/50 shadow-sm bg-white dark:bg-gray-900/50">
    <table className="min-w-full" {...props}>
      {children}
    </table>
  </div>
);

const TableHeader = ({ children, ...props }) => (
  <thead className="bg-gray-50 dark:bg-gray-800/80" {...props}>
    {children}
  </thead>
);

const TableHeaderCell = ({ children, ...props }) => (
  <th 
    className="px-4 py-3 text-left text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wide border-b border-gray-200 dark:border-gray-700"
    {...props}
  >
    {children}
  </th>
);

const TableBody = ({ children, ...props }) => (
  <tbody className="divide-y divide-gray-100 dark:divide-gray-800" {...props}>
    {children}
  </tbody>
);

const TableRow = ({ children, ...props }) => (
  <tr className="hover:bg-gray-50/80 dark:hover:bg-gray-800/40 transition-colors" {...props}>
    {children}
  </tr>
);

const TableCell = ({ children, ...props }) => (
  <td className="px-4 py-3.5 text-sm text-gray-600 dark:text-gray-300" {...props}>
    {children}
  </td>
);

// Custom Blockquote Component - styled for citations
const BlockquoteComponent = ({ children, ...props }) => (
  <blockquote 
    className="relative border-l-2 border-blue-500/60 dark:border-blue-400/60 pl-5 pr-4 py-4 my-6 bg-gradient-to-r from-gray-50 to-transparent dark:from-gray-800/40 dark:to-transparent text-gray-600 dark:text-gray-300 rounded-r-lg italic"
    {...props}
  >
    <div className="absolute -left-1 top-4 w-2 h-2 bg-blue-500 dark:bg-blue-400 rounded-full"></div>
    <div className="space-y-2 text-[15px] leading-relaxed">
      {children}
    </div>
  </blockquote>
);

// Custom Link Component with Security
const LinkComponent = ({ href, children, ...props }) => {
  const isExternal = href && (href.startsWith('http') || href.startsWith('https'));
  
  return (
    <a
      href={href}
      className="text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300 underline transition-colors"
      target={isExternal ? '_blank' : undefined}
      rel={isExternal ? 'noopener noreferrer' : undefined}
      {...props}
    >
      {children}
    </a>
  );
};

// Custom Image Component
const ImageComponent = ({ src, alt, ...props }) => {
  const [imageError, setImageError] = useState(false);
  
  if (imageError) {
    return (
      <div className="my-4 p-4 border border-gray-200 dark:border-gray-700 rounded-lg bg-gray-50 dark:bg-gray-800 text-center">
        <span className="text-gray-500 dark:text-gray-400">Failed to load image: {alt}</span>
      </div>
    );
  }
  
  return (
    <div className="my-4">
      <img
        src={src}
        alt={alt}
        className="max-w-full h-auto rounded-lg shadow-sm"
        onError={() => setImageError(true)}
        {...props}
      />
    </div>
  );
};

// Streaming Animation Hook
const useStreamingAnimation = (content, isStreaming, animationConfig = {}) => {
  const [displayedContent, setDisplayedContent] = useState('');
  const [currentIndex, setCurrentIndex] = useState(0);
  const intervalRef = useRef(null);
  
  const {
    enabled = true,
    speed = 50,
    tokenize = 'word' // 'word' or 'char'
  } = animationConfig;

  useEffect(() => {
    if (!enabled || !isStreaming) {
      setDisplayedContent(content);
      return;
    }

    const tokens = tokenize === 'char' 
      ? content.split('') 
      : content.split(/(\s+)/);
    
    if (currentIndex < tokens.length) {
      intervalRef.current = setTimeout(() => {
        setDisplayedContent(tokens.slice(0, currentIndex + 1).join(''));
        setCurrentIndex(prev => prev + 1);
      }, speed);
    }

    return () => {
      if (intervalRef.current) {
        clearTimeout(intervalRef.current);
      }
    };
  }, [content, currentIndex, isStreaming, enabled, speed, tokenize]);

  useEffect(() => {
    if (!isStreaming) {
      setDisplayedContent(content);
      setCurrentIndex(0);
    }
  }, [isStreaming, content]);

  return displayedContent;
};

// Main ChatMarkdown Component
const ChatMarkdown = ({ 
  content = '', 
  isStreaming = false, 
  className = '',
  animation = { enabled: false, speed: 30, tokenize: 'word' },
  allowedImagePrefixes = ['*'],
  allowedLinkPrefixes = ['*'],
  theme = 'dark'
}) => {
  const animatedContent = useStreamingAnimation(content, isStreaming, animation);
  
  // Security: Filter unsafe URLs
  const isUrlAllowed = (url, allowedPrefixes) => {
    if (allowedPrefixes.includes('*')) return true;
    return allowedPrefixes.some(prefix => url.startsWith(prefix));
  };

  const components = {
    code: CodeBlock,
    table: TableComponent,
    thead: TableHeader,
    th: TableHeaderCell,
    tbody: TableBody,
    tr: TableRow,
    td: TableCell,
    blockquote: BlockquoteComponent,
    a: ({ href, children, ...props }) => {
      if (href && !isUrlAllowed(href, allowedLinkPrefixes)) {
        return <span className="text-red-500">[Blocked Link: {href}]</span>;
      }
      return <LinkComponent href={href} {...props}>{children}</LinkComponent>;
    },
    img: ({ src, alt, ...props }) => {
      if (src && !isUrlAllowed(src, allowedImagePrefixes)) {
        return <span className="text-red-500">[Blocked Image: {alt}]</span>;
      }
      return <ImageComponent src={src} alt={alt} {...props} />;
    },
    h1: ({ children, ...props }) => (
      <h1 
        className="text-2xl font-bold mt-6 mb-4 pb-2 text-gray-900 dark:text-white border-b border-gray-200 dark:border-gray-700/50 tracking-tight" 
        {...props}
      >
        {children}
      </h1>
    ),
    h2: ({ children, ...props }) => (
      <h2 
        className="text-xl font-semibold mt-6 mb-3 text-gray-900 dark:text-white flex items-center gap-2 tracking-tight"
        {...props}
      >
        <span className="w-1 h-5 bg-gradient-to-b from-blue-500 to-indigo-500 rounded-full"></span>
        {children}
      </h2>
    ),
    h3: ({ children, ...props }) => (
      <h3 
        className="text-lg font-semibold mt-5 mb-2 text-gray-800 dark:text-gray-100 tracking-tight"
        {...props}
      >
        {children}
      </h3>
    ),
    h4: ({ children, ...props }) => (
      <h4 
        className="text-base font-semibold mt-4 mb-2 text-gray-700 dark:text-gray-200"
        {...props}
      >
        {children}
      </h4>
    ),
    p: ({ children, ...props }) => (
      <p 
        className="mb-4 text-gray-600 dark:text-gray-300 leading-7 text-[15px]" 
        {...props}
      >
        {children}
      </p>
    ),
    ul: ({ children, ...props }) => (
      <ul 
        className="mb-4 ml-1 space-y-2 text-gray-600 dark:text-gray-300" 
        {...props}
      >
        {children}
      </ul>
    ),
    ol: ({ children, ...props }) => (
      <ol 
        className="mb-4 ml-1 space-y-2 text-gray-600 dark:text-gray-300 list-none counter-reset-item" 
        style={{ counterReset: 'item' }}
        {...props}
      >
        {children}
      </ol>
    ),
    li: ({ children, ordered, ...props }) => (
      <li 
        className="flex items-start gap-3 text-[15px] leading-7"
        {...props}
      >
        <span className="flex-shrink-0 w-1.5 h-1.5 mt-2.5 rounded-full bg-gradient-to-br from-blue-400 to-indigo-500"></span>
        <span className="flex-1">{children}</span>
      </li>
    ),
    strong: ({ children, ...props }) => (
      <strong 
        className="font-semibold text-gray-900 dark:text-white" 
        {...props}
      >
        {children}
      </strong>
    ),
    em: ({ children, ...props }) => (
      <em 
        className="italic text-gray-700 dark:text-gray-200" 
        {...props}
      >
        {children}
      </em>
    ),
    hr: ({ ...props }) => (
      <hr 
        className="my-8 border-0 h-px bg-gradient-to-r from-transparent via-gray-300 dark:via-gray-600 to-transparent" 
        {...props} 
      />
    ),
  };

  return (
    <div className={`chat-markdown max-w-none ${className}`}>
      <ReactMarkdown
        components={components}
        remarkPlugins={[remarkGfm, remarkMath]}
        rehypePlugins={[rehypeKatex, rehypeHighlight]}
      >
        {animatedContent}
      </ReactMarkdown>
    </div>
  );
};

export default ChatMarkdown;
